import { CodeNameModel, FileList } from 'components/common/types';
import { Environment, RouteFrameCode, TagInclude } from 'components/common/types/Deal.types';
import {
  DirectSalesCodeNameModel,
  DirectSalesLineObjective,
  DirectSalesMediaType,
  DirectSalesPricingMode,
} from 'components/common/types/DirectSalesCampaign.types';
import { NetworkCriteriaType } from 'components/common/types/DirectSalesNetworks.types';
import { ProductTemplate } from 'components/common/types/DirectSalesProductTemplate.types';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { POI as PoiType } from 'components/common/types/Planner.types';
import {
  ProductSotType,
  ProductSweepType,
} from 'components/pages/PCM/Products/ProductFields/ProductShareOfTime/ProductShareOfTime.types';

export enum ProductFieldName {
  OBJECTIVES = 'objectives',
  NETWORKS = 'networks',
  PRICING_MODE = 'pricingMode',
  RANGE = 'range',
  FORMATS = 'formats',
  MEDIA_TYPE = 'mediaType',
  TARGETS = 'targets',
  CHANNELS = 'channels',
  LOCATIONS = 'locations',
  FRAME_LIST = 'frameList',
  POI = 'poi',
  TAGS = 'tags',
  SOT = 'sot',
  SWEEP = 'sweep',
}

export type ProductLine = {
  lineId: string;
  name: string;
} & ProductManagementFields;

export enum SelectionPermission {
  ALL_OPTIONS = 'ALL_OPTIONS',
  FIXED_VALUE = 'FIXED_VALUE',
  DEFAULT_VALUE = 'DEFAULT_VALUE',
  DISABLED = 'DISABLED',
  ALLOWED_VALUES = 'ALLOWED_VALUES',
  RANGE_VALUE = 'RANGE_VALUE',
}

export type NewProductCreationPayload = {
  selectedPermission: CodeNameModel;
  fieldName: string;
  value?:
    | DirectSalesMediaType
    | DirectSalesPricingMode
    | Environment[]
    | DirectSalesCodeNameModel[]
    | ProductScheduleRange
    | NetworkCriteriaType[]
    | DirectSalesLineObjective[]
    | { routeFrameCodes: RouteFrameCode[]; listFiles: FileList[] }
    | PoiType[]
    | TagInclude[]
    | ProductSotType
    | string;
};

export type ClearNewProductCreationPayload = {
  fieldName: string;
};

export type ClearSingleTargetPayload = {
  targetName: string;
};

export type ProductScheduleRange = {
  startDate?: Date;
  endDate?: Date;
};

export type ProductTarget = {
  selectionPermission: SelectionPermission;
  value?: number;
  priority?: DirectSalesCodeNameModel;
  greenTolerance?: number;
  orangeTolerance?: number;
  minValue?: number;
  maxValue?: number;
  defaultValue?: number;
};

export type ProductTargets = {
  budget?: ProductTarget;
  impressions?: ProductTarget;
  frames?: ProductTarget;
};

export interface ProductManagementFields {
  mediaType?: {
    selectionPermission: SelectionPermission;
    value?: DirectSalesMediaType;
  };
  pricingMode?: {
    selectionPermission: SelectionPermission;
    value?: DirectSalesPricingMode;
  };
  range?: {
    selectionPermission: SelectionPermission;
    value?: ProductScheduleRange;
  };
  channels?: {
    selectionPermission: SelectionPermission;
    value?: Environment[];
  };
  locations?: {
    selectionPermission: SelectionPermission;
    value?: DirectSalesCodeNameModel[];
  };
  formats?: {
    selectionPermission: SelectionPermission;
    value?: DirectSalesCodeNameModel[];
  };
  poi?: {
    selectionPermission: SelectionPermission;
    value?: PoiType[];
  };
  objectives: {
    selectionPermission: SelectionPermission;
    value: DirectSalesLineObjective[];
  };
  budget?: ProductTarget;
  frames?: ProductTarget;
  impressions?: ProductTarget;
  networks?: {
    selectionPermission: SelectionPermission;
    value?: NetworkCriteriaType[];
  };
  frameList?: {
    selectionPermission: SelectionPermission;
    value?: { routeFrameCodes: RouteFrameCode[]; listFiles: FileList[]; mapRouteFrameCodes?: RouteFrameCode[] };
  };
  tags?: {
    selectionPermission: SelectionPermission;
    value?: TagInclude[];
  };
  sot?: {
    selectionPermission: SelectionPermission;
    value?: ProductSotType;
  };
  sweep?: {
    selectionPermission: SelectionPermission;
    value?: ProductSweepType;
  };
}

export interface NewProduct extends ProductManagementFields {
  id: string;
  name: string;
  folder: string;
}

export interface ProductManagement {
  newProduct: NewProduct;
  productLines: ProductLine[];
  activeLineId: string;
  selectedProducts: ProductTemplate[];
  secondaryPanel: PlannerSecondaryPanelType;
  originalProductId?: string;
  intendedOriginalProductState?: string;
  expandedFolders: string[];
  isSavingProduct: boolean;
  isReadOnly: boolean;
}
